import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const VARIABLES = [
  { nombre: '{{ descuento_display }}', desc: 'Ej: "15%" o "$50.000"' },
  { nombre: '{{ codigo }}',            desc: 'Código de descuento de la campaña' },
  { nombre: '{{ asunto }}',            desc: 'Asunto del email' },
  { nombre: '{{ countdown_url }}',     desc: 'URL del GIF contador' },
  { nombre: '{{ fecha_expiracion_texto }}', desc: 'Ej: "15 de junio de 2026"' },
  { nombre: '{{ url_token }}',         desc: 'Enlace personalizado del destinatario' },
  { nombre: '{{ unsubscribe_url }}',   desc: 'Enlace para darse de baja' },
];

const PREVIEW_VARS = {
  '{{ descuento_display }}': '15%',
  '{{ codigo }}':            'SOLAR2026',
  '{{ asunto }}':            'Tu descuento exclusivo de FilmSolar',
  '{{ countdown_url }}':     '',
  '{{ fecha_expiracion_texto }}': '15 de junio de 2026',
  '{{ url_token }}':         '#',
  '{{ unsubscribe_url }}':   '#',
};

const renderPreview = (html, campana) => {
  let result = html;
  const vars = {
    ...PREVIEW_VARS,
    '{{ asunto }}': campana?.asunto || PREVIEW_VARS['{{ asunto }}'],
    '{{ codigo }}': campana?.codigo || PREVIEW_VARS['{{ codigo }}'],
    '{{ countdown_url }}': campana?.countdown_url || '',
    '{{ descuento_display }}': campana
      ? campana.tipo_descuento === 'porcentaje'
        ? `${campana.valor_descuento}%`
        : `$${Number(campana.valor_descuento).toLocaleString('es-CL')}`
      : '15%',
  };
  // Replace Django template variables
  for (const [key, val] of Object.entries(vars)) {
    result = result.split(key).join(val);
  }
  // Strip simple block tags that can't render without context
  result = result.replace(/\{%[^%]*%\}/g, '');
  return result;
};

const CargarTemplateHtml = ({ campana, onClose, onSuccess }) => {
  const [html, setHtml] = useState(campana?.html_template || '');
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);
  const iframeRef = useRef(null);

  const handleFileLoad = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setHtml(ev.target.result || '');
    reader.readAsText(file, 'UTF-8');
    e.target.value = '';
  };

  useEffect(() => {
    if (!showPreview || !iframeRef.current) return;
    const doc = iframeRef.current.contentDocument || iframeRef.current.contentWindow?.document;
    if (doc) {
      doc.open();
      doc.write(renderPreview(html, campana));
      doc.close();
    }
  }, [showPreview, html, campana]);

  const handleUseTemplate = async () => {
    setLoadingTemplate(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/template-base/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHtml(res.data.html || '');
      setShowPreview(false);
    } catch {
      setError('No se pudo cargar el template base. Intenta de nuevo.');
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleSave = async () => {
    if (!html.trim()) {
      setError('El template no puede estar vacío.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_URL}/api/campanas/${campana.id}/`,
        { html_template: html },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSuccess();
    } catch (err) {
      const detail = err.response?.data;
      setError(
        typeof detail === 'string'
          ? detail
          : Object.values(detail || {}).flat().join(' ') || 'Error al guardar el template.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-3xl shadow-xl flex flex-col max-h-[92vh]">

        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Template HTML del email</h2>
            <p className="text-sm text-gray-500 mt-0.5">Carga el HTML que se usará para los emails de esta campaña</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
        </div>

        <div className="p-6 flex flex-col gap-5 overflow-y-auto flex-1 min-h-0">

          {/* Variables disponibles */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Variables disponibles</p>
            <div className="flex flex-wrap gap-2">
              {VARIABLES.map(({ nombre, desc }) => (
                <button
                  key={nombre}
                  type="button"
                  title={desc}
                  onClick={() => navigator.clipboard?.writeText(nombre)}
                  className="font-mono text-xs bg-orange-50 border border-orange-200 text-orange-700 px-2 py-1 rounded hover:bg-orange-100 transition-colors"
                >
                  {nombre}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-1.5">Haz clic en una variable para copiarla al portapapeles.</p>
          </div>

          {/* Cargar archivo */}
          <div className="flex items-center gap-3 flex-wrap">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50 font-medium"
            >
              Cargar archivo .html
            </button>
            <button
              type="button"
              onClick={handleUseTemplate}
              disabled={loadingTemplate}
              className="px-4 py-2 rounded-lg border border-orange-300 text-orange-700 text-sm hover:bg-orange-50 font-medium disabled:opacity-50"
            >
              {loadingTemplate ? 'Cargando...' : 'Usar template base'}
            </button>
            {html && (
              <span className="text-xs text-green-600 font-medium">
                {html.length.toLocaleString()} caracteres cargados
              </span>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".html,.htm"
              className="hidden"
              onChange={handleFileLoad}
            />
          </div>

          {/* Textarea del HTML */}
          <div className="flex flex-col gap-1 flex-1 min-h-0">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-gray-700">Código HTML</label>
              <button
                type="button"
                onClick={() => setShowPreview((v) => !v)}
                className="text-xs text-orange-600 hover:text-orange-800 underline font-medium"
              >
                {showPreview ? 'Ocultar vista previa' : 'Ver vista previa'}
              </button>
            </div>
            <textarea
              value={html}
              onChange={(e) => { setHtml(e.target.value); setShowPreview(false); }}
              className="w-full border border-gray-300 rounded-lg p-3 text-xs font-mono resize-none focus:outline-none focus:ring-2 focus:ring-orange-300 flex-1"
              style={{ minHeight: showPreview ? 120 : 260 }}
              placeholder="Pega el HTML del email aquí, o usa el botón para cargar un archivo..."
              spellCheck={false}
            />
          </div>

          {/* Vista previa */}
          {showPreview && html.trim() && (
            <div className="flex flex-col gap-1">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Vista previa — variables reemplazadas con valores de ejemplo
              </p>
              <div className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50" style={{ height: 340 }}>
                <iframe
                  ref={iframeRef}
                  title="preview-template"
                  className="w-full h-full border-0"
                  sandbox="allow-same-origin"
                />
              </div>
            </div>
          )}

          {error && (
            <p className="text-red-600 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-3 justify-end p-6 border-t flex-shrink-0">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !html.trim()}
            className="px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#FF743A' }}
          >
            {saving ? 'Guardando...' : 'Guardar template'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CargarTemplateHtml;
